import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';

interface NameCheckProps {
  onSubmit: (member: { id: string; name: string; }) => void;
  onCancel?: () => void;
}

interface Member {
  id: string;
  name: string;
}

export function NameCheck({ onSubmit, onCancel }: NameCheckProps) {
  const [name, setName] = useState('');
  const [suggestions, setSuggestions] = useState<Member[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (name.trim().length === 0) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      const searchTerm = name.trim().toLowerCase();

      setLoading(true);
      try {
        // First try exact match
        const { data, error } = await supabase
          .from('members')
          .select('id, name, season')
          .eq('name', searchTerm)
          .order('name')
          .maybeSingle();

        if (error && !error.code?.includes('PGRST116')) {
          throw error;
        }

        // If no exact match found, try partial match
        if (!data || data.length === 0) {
          const { data: partialData } = await supabase
            .from('members')
            .select('id, name, season')
            .ilike('name', `%${searchTerm}%`)
            .order('name');
            
          setSuggestions(partialData || []);
          setShowSuggestions((partialData || []).length > 0);
        } else {
          setSuggestions([data]);
          setShowSuggestions(true);
        }

      } catch (error) {
        console.error('Error fetching suggestions:', error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(debounceTimer);
  }, [name]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    // For new members, don't generate an ID - it will be created by Supabase
    onSubmit({ id: '', name: name.trim() });
    setShowSuggestions(false);
  };

  const handleSuggestionClick = (suggestion: Member) => {
    onSubmit(suggestion);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  return (
    <div>
      <h2 className="text-xl font-semibold mb-4">Member Registration</h2>
      <form onSubmit={handleSubmit} className="space-y-4 relative">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
            Full Name
          </label>
          <div className="relative">
            <input
              type="text"
              id="name"
              autoFocus
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 relative z-50"
              placeholder="Enter your full name"
              required
              autoComplete="off"
            />
            {loading && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
              </div>
            )}
            {showSuggestions && suggestions.length > 0 && (
              <div
                ref={dropdownRef}
                className="absolute left-0 right-0 mt-1 bg-white rounded-md shadow-lg border border-gray-200 max-h-60 overflow-auto z-[60] w-full"
                style={{ minWidth: '200px' }}
              >
                {suggestions.map((suggestion) => (
                  <button
                    key={suggestion.id}
                    type="button"
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-primary-50 focus:bg-primary-50 focus:outline-none flex items-center justify-between"
                  >
                    <span>{suggestion.name}</span>
                    <span className="text-xs text-primary-600">Select</span>
                  </button>
                ))}
              </div>
            )}
            <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
              <span>Start typing to search for existing members</span>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Continue
          </button>
        </div>
      </form>
    </div>
  );
}